import { v4 as uuid } from "uuid";
import { z } from "zod";
import { PackageType, packageProgressSchema } from "../../entities/contractorPackage";
import { dateToString } from "../../utils/date";
import { contractorPackageLocalContentTargetLineItemSchema } from "../contractorPackageLocalContentTarget";
import { packageEnvironmentalCommitmentLineItemSchema } from "../environmentalCommitment";
import { createDefaultFinancial, financialLineItemSchema } from "../financial";
import { cloneProjectLabourHourCommitmentsForPackage, cloneProjectLabourHourCommitmentsFromPackage, packageLabourHourCommitmentLineItemSchema } from "../packageLabourHourCommitment";
import { packageLocalContentLineItemSchema } from "../packageLocalContent";
import { createDefaultPackagePolicy, packagePolicyLineItemSchema } from "../packagePolicy";
import { cloneProjectSpendCommitmentsForPackage, cloneProjectSpendCommitmentsFromPackage, packageSocialSpendCommitmentLineItemSchema } from "../packageSocialSpendCommitment";
/**
 * Draft line item for a new **root** package (no `parentPackageId`). `treePath` is a single
 * segment equal to the new package id, matching backend `resolvePathForNewPackage` when
 * there is no parent.
 *
 * For a **child** package under an existing line item (dot-`treePath`, parent id, cloned
 * commitments/targets), use {@link createInheritedContractorPackage} with a base from this helper.
 */
export const createDefaultContractorPackage = (project, packageType, packageCount, contractor) => {
    const packageId = uuid();
    const packageCountString = packageCount.toString().trim();
    // Use the current month, unless it's before the project start date
    const currentMonth = dateToString(new Date());
    const packageStartDate = currentMonth > project.projectStartDate
        ? currentMonth
        : project.projectStartDate;
    return {
        closedDate: undefined,
        commitmentsEnvironmental: (project.commitmentsEnvironmental || []).map((c) => ({
            contractorPackageId: packageId,
            created: new Date(),
            id: uuid(),
            projectEnvironmentalCommitmentId: c.id,
            projectId: project.id
        })),
        commitmentsHours: cloneProjectLabourHourCommitmentsForPackage(packageId, project.commitmentsHours),
        commitmentsSpend: cloneProjectSpendCommitmentsForPackage(packageId, project.commitmentsSpend),
        contractorAbn: contractor?.abn || "",
        contractorDisplayName: contractor?.displayName || "",
        contractorId: contractor?.id,
        contractorLogo: contractor?.organisationLogo || "",
        contractorName: contractor?.organisationName || "",
        created: new Date(),
        depth: 1,
        estimatedProgress: {
            accepted: 0,
            complete: 0,
            overdue: 0
        },
        financial: createDefaultFinancial(),
        id: packageId,
        isAccepted: false,
        isClosed: false,
        isDraft: true,
        localContent: [],
        localContentTargets: [],
        packageNumber: `${packageCountString.padStart(3, "0")}`,
        packageStartDate: packageStartDate,
        packageType: packageType,
        policies: project.policies.map((p) => createDefaultPackagePolicy(project.id, packageId, p.id)),
        projectId: project.id,
        representativeEmail: "",
        treePath: packageId,
        useLabourHour: true,
        useLocalContent: true,
        usePolicies: true,
        useSocialSpend: true,
        useSustainability: true
    };
};
/** Child package under `parentPackage`: sets `parentPackageId` and `treePath` to `parent.treePath.childId`. */
export const createInheritedContractorPackage = (basePackage, parentPackage) => ({
    ...basePackage,
    commitmentsEnvironmental: (parentPackage.commitmentsEnvironmental || []).map((commitment) => ({
        ...commitment,
        contractorPackageId: basePackage.id,
        created: new Date(),
        id: uuid()
    })),
    commitmentsHours: cloneProjectLabourHourCommitmentsFromPackage(basePackage.id, parentPackage.commitmentsHours),
    commitmentsSpend: cloneProjectSpendCommitmentsFromPackage(basePackage.id, parentPackage.commitmentsSpend),
    depth: parentPackage.depth + 1,
    enabledLocalContent: parentPackage.enabledLocalContent
        ? [...parentPackage.enabledLocalContent]
        : undefined,
    localContentTargets: parentPackage.localContentTargets.map((target) => ({
        ...target,
        contractorPackageId: basePackage.id,
        created: new Date(),
        id: uuid()
    })),
    packageStartDate: parentPackage.packageStartDate,
    packageType: parentPackage.packageType,
    parentPackageId: parentPackage.id,
    policies: parentPackage.policies.map((policy) => ({
        ...policy,
        contractorPackageId: basePackage.id,
        created: new Date(),
        id: uuid()
    })),
    treePath: `${parentPackage.treePath}.${basePackage.id}`
});
export const contractorPackageLineItemSchema = z.object({
    closedDate: z.string().optional(),
    commitmentsEnvironmental: z.array(packageEnvironmentalCommitmentLineItemSchema),
    commitmentsHours: z.array(packageLabourHourCommitmentLineItemSchema),
    commitmentsSpend: z.array(packageSocialSpendCommitmentLineItemSchema),
    contractorAbn: z.string(),
    /**
     * Label to render wherever the organisation is named. Canonical form of
     * `contractorName` with shouted casing normalised, falling back to it.
     * Keep using the raw name on contracts, reports and exports.
     */
    contractorDisplayName: z.string(),
    contractorId: z.string().optional(),
    contractorLogo: z.string(),
    contractorName: z.string(),
    created: z.date(),
    depth: z.number(),
    enabledLocalContent: z.array(z.string()).optional(),
    estimatedProgress: packageProgressSchema,
    financial: financialLineItemSchema,
    /**
     * Earliest `report_id` (reporting month) of an **Accepted** report submission across this
     * package and its descendant subtree, as an ISO date string. `undefined` when no accepted
     * report exists yet. The package start date may be moved earlier freely but must not be set
     * after this month, or that month's reported data would fall outside the package range.
     */
    firstReportMonth: z.string().optional(),
    id: z.string(),
    isAccepted: z.boolean(),
    isClosed: z.boolean(),
    isDraft: z.boolean(),
    localContent: z.array(packageLocalContentLineItemSchema),
    localContentTargets: z.array(contractorPackageLocalContentTargetLineItemSchema),
    packageContact: z.string().optional(),
    packageDescription: z.string().optional(),
    packageEndDate: z.string().optional(),
    packageNumber: z.string(),
    packageStartDate: z.string(),
    packageType: z.enum(PackageType),
    parentPackageId: z.string().optional(),
    policies: z.array(packagePolicyLineItemSchema),
    projectId: z.string(),
    reportExists: z.boolean().optional(),
    representativeEmail: z.string(),
    sendInvite: z.boolean().optional(),
    treePath: z.string(),
    useLabourHour: z.boolean(),
    useLocalContent: z.boolean(),
    usePolicies: z.boolean(),
    useSocialSpend: z.boolean(),
    useSustainability: z.boolean()
});
export const packageTypeToString = (packageType) => {
    if (packageType === undefined) {
        return "";
    }
    switch (packageType) {
        default:
            // A display formatter must never crash the render. Unknown/legacy enum
            // values — and the free-form labels some org-insights rows carry — pass
            // through as-is (callers pair this with `|| row.type` for the same reason).
            return packageType;
        case PackageType.OneTime:
            return "One Time";
        case PackageType.HeadContractorPackage:
            return "Head Contractor Package";
        case PackageType.BalustradesAndHandrail:
            return "Balustrades & Handrail";
        case PackageType.Carpentry:
            return "Carpentry";
        case PackageType.ConcretingAndFormwork:
            return "Concreting & Formwork";
        case PackageType.Consultants:
            return "Consultants";
        case PackageType.CurtainsAndBlinds:
            return "Curtains & Blinds";
        case PackageType.Civil:
            return "Civil";
        case PackageType.Demolition:
            return "Demolition";
        case PackageType.Design:
            return "Design";
        case PackageType.DryFire:
            return "Dry Fire";
        case PackageType.Electrical:
            return "Electrical";
        case PackageType.Excavating:
            return "Excavating";
        case PackageType.Facade:
            return "Facade";
        case PackageType.FacadeAndCladding:
            return "Facade & Cladding";
        case PackageType.Fencing:
            return "Fencing";
        case PackageType.Flooring:
            return "Flooring";
        case PackageType.Hydraulics:
            return "Hydraulics";
        case PackageType.KitchensAndBars:
            return "Kitchens & Bars";
        case PackageType.LabourHire:
            return "Labour Hire";
        case PackageType.Landscaping:
            return "Landscaping";
        case PackageType.Masonry:
            return "Masonry";
        case PackageType.Mechanical:
            return "Mechanical";
        case PackageType.MechanicalGas:
            return "Mechanical Gas";
        case PackageType.Miscellaneous:
            return "Miscellaneous";
        case PackageType.Other:
            return "Other";
        case PackageType.Painting:
            return "Painting";
        case PackageType.Plumbing:
            return "Plumbing";
        case PackageType.Plastering:
            return "Plastering";
        case PackageType.RoofSafetySystems:
            return "Roof Safety Systems";
        case PackageType.Roofing:
            return "Roofing";
        case PackageType.Scaffolding:
            return "Scaffolding";
        case PackageType.Security:
            return "Security";
        case PackageType.Signage:
            return "Signage";
        case PackageType.Structure:
            return "Structure";
        case PackageType.StructuralAndFraming:
            return "Structural & Framing";
        case PackageType.StructuralSteel:
            return "Structural Steel";
        case PackageType.Tiling:
            return "Tiling";
        case PackageType.WetFire:
            return "Wet Fire";
    }
};
